import type { AccountCookiePreference } from './accountInfo';

// Cookie consent state — pure logic, no React / no analytics side effects.
// The US "opt-out" model: analytics scripts may load by default, but the user
// must have a real, working way to opt out, and we honor GPC + a stored choice
// on every load. See src/components/feature/CookieConsent.tsx for the UI and
// src/features/analytics.ts for how the choice actually gates GA + Clarity.

export type ConsentCategories = {
  // Strictly-necessary is implicit/always true and is not stored here.
  analytics: boolean;
  functional: boolean;
};

export type StoredConsent = {
  version: number;
  timestamp: string;
  categories: ConsentCategories;
};

// Bump when the policy/categories change so users are re-prompted.
export const CONSENT_VERSION = 1;
export const CONSENT_STORAGE_KEY = 'vadauro_cookie_consent';

// Fired on `window` to open the preferences modal from anywhere (footer links,
// in-policy links). `detail.focus === 'analytics'` asks the modal to focus the
// analytics toggle (used by the "Do Not Sell or Share" link).
export const OPEN_COOKIE_PREFERENCES_EVENT = 'vadauro:open-cookie-preferences';

export type OpenCookiePreferencesDetail = { focus?: 'analytics' };

export function accountCookiesToConsent(
  cookies: AccountCookiePreference[] | null | undefined
): ConsentCategories | null {
  if (cookies === null || cookies === undefined) {
    return null;
  }

  return {
    analytics: cookies.includes('performance'),
    functional: cookies.includes('functional'),
  };
}

export function consentToAccountCookies(consent: ConsentCategories): AccountCookiePreference[] {
  const cookies: AccountCookiePreference[] = [];

  if (consent.functional) {
    cookies.push('functional');
  }
  if (consent.analytics) {
    cookies.push('performance');
  }

  return cookies;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function isGpcEnabled(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

export function readStoredConsent(): StoredConsent | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    // Re-prompt when the stored version predates the current policy version.
    if (!parsed || parsed.version !== CONSENT_VERSION || !parsed.categories) {
      return null;
    }

    return {
      version: CONSENT_VERSION,
      timestamp: typeof parsed.timestamp === 'string' ? parsed.timestamp : new Date().toISOString(),
      categories: {
        analytics: parsed.categories.analytics === true,
        functional: parsed.categories.functional === true,
      },
    };
  } catch {
    return null;
  }
}

export function persistConsent(categories: ConsentCategories): StoredConsent {
  const record: StoredConsent = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      analytics: categories.analytics === true,
      functional: categories.functional === true,
    },
  };

  const storage = getStorage();
  if (storage) {
    try {
      storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Storage may be unavailable (private mode / quota) — the choice still
      // applies for the current session via applyConsent().
    }
  }

  return record;
}

export function hasStoredConsent(): boolean {
  return readStoredConsent() !== null;
}

// Default when the user has not made an explicit choice yet.
// Opt-out model: analytics on by default — UNLESS GPC asks us to opt out.
export function getDefaultConsent(): ConsentCategories {
  const gpc = isGpcEnabled();
  return {
    analytics: !gpc,
    functional: true,
  };
}

// The consent we should honor on this load: an explicit stored choice wins,
// otherwise the GPC-aware default.
export function getEffectiveConsent(): ConsentCategories {
  const stored = readStoredConsent();
  if (isGpcEnabled()) {
    return {
      analytics: false,
      functional: stored?.categories.functional ?? true,
    };
  }
  if (stored) {
    return stored.categories;
  }
  return getDefaultConsent();
}
